import React, { useEffect, useState, Component } from 'react';
import { HomePage } from './pages/HomePage';
import { ShopAllProductsPage } from './pages/ShopAllProductsPage';
import { Gen3Page } from './pages/Gen3Page';
import { DaedoPSSSystemPage } from './pages/DaedoPSSSystemPage';
import { WacokuProductsPage } from './pages/WacokuProductsPage';
import { CategoryPage } from './pages/CategoryPage';
import { CATEGORIES } from './data/categories';
import { ComingSoonPage } from './pages/ComingSoonPage';
import { ThresholdsPage } from './pages/ThresholdsPage';
import { EFootPage } from './pages/EFootPage';
import { FinancingPage } from './pages/FinancingPage';
import { ReturnsPage } from './pages/ReturnsPage';
import { Gen2EChestGearPage } from './pages/Gen2EChestGearPage';
import { Gen3EFootGearPage } from './pages/Gen3EFootGearPage';
type Route = 'home' | 'shop-all' | 'gen-3' | 'daedo-pss-system' | 'wacoku-products' | 'daedo-products' | 'support-thresholds' | 'support-software' | 'support-troubleshooting' | 'support-efoot' | 'financing' | 'returns' | 'product-gen2-echest' | 'product-gen3-efoot';
function normalizePath(raw: string): string {
  let p = raw || '/';
  try {
    p = decodeURIComponent(p);
  } catch {
    /* keep as-is if malformed */
  }
  p = p.toLowerCase();
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p;
}
function routeFromPath(rawPath: string): Route | null {
  const p = normalizePath(rawPath);
  if (p === '/' || p === '/index' || p === '/index.html') return 'home';
  // Dedicated category pages (their own self-contained folders)
  if (p === '/shop') return 'shop-all';
  if (p === '/shop/gen-3') return 'gen-3';
  if (p === '/shop/daedo-pss-system') return 'daedo-pss-system';
  if (p === '/shop/wacoku-products') return 'wacoku-products';
  // Shared CategoryPage template (only daedo-products still uses it)
  if (p === '/shop/daedo-products') return 'daedo-products';
  // Legacy URL fallbacks
  if (p === '/26.html' || p === '/daedo') return 'shop-all';
  if (p === '/420m5/the-complete-gen-3-system.html') return 'gen-3';
  if (p === '/truescore-pss-system') return 'daedo-pss-system';
  if (p === '/421.html' || p === '/wacoku') return 'wacoku-products';
  if (p === '/financing.html') return 'financing';
  if (p === '/thresholds') return 'support-thresholds';
  if (p === '/download') return 'support-software';
  if (p === '/daedo-truescore-troubleshooting-video') return 'support-troubleshooting';
  if (p === '/e-footgear_usage_recommendations') return 'support-efoot';
  if (p === '/return-policy') return 'returns';
  if (p === '/m5/epro 29039--gen3-e-foot-gear.html') return 'product-gen3-efoot';
  if (p === '/e-chest-gear-adult') return 'product-gen2-echest';
  return null;
}
function routeFromHash(rawHash: string): Route | null {
  const h = rawHash.replace(/^#/, '').toLowerCase();
  if (!h) return null;
  if (h === 'shop' || h === 'shop-all') return 'shop-all';
  if (h === 'shop-gen-3' || h === 'gen-3' || h === 'gen3') return 'gen-3';
  if (h === 'shop-daedo-pss-system' || h === 'daedo-pss-system' || h === 'daedo-pss-systems' || h === 'daedo-pss') return 'daedo-pss-system';
  if (h === 'shop-daedo-products' || h === 'daedo-products') return 'daedo-products';
  if (h === 'shop-wacoku-products' || h === 'wacoku-products') return 'wacoku-products';
  if (h === 'support-thresholds') return 'support-thresholds';
  if (h === 'support-software') return 'support-software';
  if (h === 'support-troubleshooting') return 'support-troubleshooting';
  if (h === 'support-efoot') return 'support-efoot';
  if (h === 'financing') return 'financing';
  if (h === 'returns') return 'returns';
  if (h === 'product-gen2-echest') return 'product-gen2-echest';
  if (h === 'product-gen3-efoot') return 'product-gen3-efoot';
  return null;
}
function routeFromLocation(): Route {
  if (typeof window === 'undefined') return 'home';
  return routeFromPath(window.location.pathname) ?? routeFromHash(window.location.hash) ?? 'home';
}
export function App() {
  const [route, setRoute] = useState<Route>(() => routeFromLocation());
  useEffect(() => {
    const handle = () => setRoute(routeFromLocation());
    handle();
    window.addEventListener('popstate', handle);
    window.addEventListener('hashchange', handle);
    return () => {
      window.removeEventListener('popstate', handle);
      window.removeEventListener('hashchange', handle);
    };
  }, []);
  switch (route) {
    case 'shop-all':
      return <ShopAllProductsPage data-id="element-0" />;
    case 'gen-3':
      return <Gen3Page data-id="element-1" />;
    case 'daedo-pss-system':
      return <DaedoPSSSystemPage data-id="element-2" />;
    case 'wacoku-products':
      return <WacokuProductsPage data-id="element-3" />;
    case 'daedo-products':
      {
        const cat = CATEGORIES['daedo-products'];
        return <CategoryPage title={cat.title} description={cat.description} products={[...cat.products]} data-id="element-4" />;
      }
    case 'support-thresholds':
      return <ThresholdsPage data-id="element-5" />;
    case 'support-software':
      return <ComingSoonPage title="Download Scoring Software" description="Downloads for Gen2 and Gen3 scoring software will be available here soon." data-id="element-6" />;
    case 'support-troubleshooting':
      return <ComingSoonPage title="Troubleshooting Video" description="Video guides for troubleshooting your Daedo PSS equipment are coming soon." data-id="element-7" />;
    case 'support-efoot':
      return <EFootPage data-id="element-8" />;
    case 'financing':
      return <FinancingPage data-id="element-9" />;
    case 'returns':
      return <ReturnsPage data-id="element-10" />;
    case 'product-gen2-echest':
      return <Gen2EChestGearPage data-id="element-11" />;
    case 'product-gen3-efoot':
      return <Gen3EFootGearPage data-id="element-12" />;
    case 'home':
    default:
      return <HomePage data-id="element-13" />;
  }
}